import React, { useMemo } from 'react'

import { Button, Grid, Typography } from '@barracuda-internal/bds-core'
import { useFormatMessage } from 'lib/localization'
import { AvailableSettings, SettingValue } from 'types/Settings'
import { useSettingsStateLogic } from 'components/libs/useSettingsState'

import DomainTable from 'components/libs/domainTable/DomainTable'
import outboundSettingsStyles from 'components/pages/outboundSettings/outboundSettingsStyles'
import OutboundDlp from 'components/pages/support/help/outboundDlp/OutboundDlp'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import SettingsRowRadio from 'components/libs/settings/SettingsRowRadio'
import SettingsRowHelper from 'components/libs/settings/SettingsRowHelper'
import { PageTitle, FormSpacer } from 'components/libs/layout/pages/pageLayout'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { useEncryptionLogic } from './useEncryptionLogic'

const BASE_I18N_KEY = 'ess.outbound_settings.encryption'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'

const Encryption: React.FC = () => {
  const isReadonly = useIsReadonly()
  const classes = outboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)

  const [state, eventHandlers] = useEncryptionLogic()
  const [UseGetSettingsStateLogic] = useSettingsStateLogic()

  return useMemo(
    () => (
      <SettingsPageContainer
        key={state.key}
        isLoading={UseGetSettingsStateLogic.isLoading}
        isFailedToLoad={UseGetSettingsStateLogic.isFailedToLoad}
        dirtyCheck
        isDirtyForm={state.hasPageChanges}
      >
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={eventHandlers.helpConfig.onHelpClick}
          onCancel={eventHandlers.onCancelConfirm}
          onSubmit={eventHandlers.onSave}
          submitInProgress={state.isUpdateInProgress}
          isDisabledSubmit={!state.hasPageChanges}
          help={{
            content: OutboundDlp,
            onClose: eventHandlers.helpConfig.onCloseHelp,
            title: TOPICS.dlpEncryption,
            group: GROUP_TITLE.outboundSettings,
            isHelpOpen: eventHandlers.helpConfig.isOpen
          }}
        />
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <>
            <FormSpacer />
            <Grid container direction="row">
              <SettingsRowRadio
                name={AvailableSettings.OUTBOUND_TLS_ALL}
                onChange={eventHandlers.onFormChange}
                options={[
                  { label: formatMessage('all_messages'), value: SettingValue.ENABLED },
                  { label: formatMessage('selected_domains'), value: SettingValue.DISABLED }
                ]}
                value={state.tlsAll}
                labelColumnSize={1}
                radioDirection="column"
                disabled={isReadonly}
              />

              {state.tlsChanged && (
                <SettingsRowHelper textVariant="h5" labelColumnSize={1} text={formatMessage('save_changes_helper')} />
              )}

              <Grid item xs={12} className={classes.topSpacing}>
                <Grid container direction="row" className={classes.flexWrapper}>
                  <Grid>
                    <Typography variant="body1">
                      {state.tlsAll === SettingValue.ENABLED
                        ? formatMessage('table_description_all', {
                            b: (txt: any) => <b>{txt}</b>
                          })
                        : formatMessage('table_description_select')}
                    </Typography>
                    <Typography variant="body1">{formatMessage('table_sub_description')}</Typography>
                  </Grid>
                  <Button
                    disabled={state.tlsChanged || isReadonly}
                    className={classes.bulkEdit}
                    onClick={eventHandlers.onBulkEdit}
                    variant="contained"
                    size="small"
                    data-testid="bulk-button"
                  >
                    {formatInboundMessage('bulk_edit')}
                  </Button>
                </Grid>

                <DomainTable
                  data={state.data}
                  onAddItem={eventHandlers.onAddItem}
                  onRemoveItem={eventHandlers.onRemoveItem}
                  data-testid="domain-table"
                  delegateDirtyFormState={eventHandlers.setIsEncryptionTableDirty}
                />
              </Grid>
            </Grid>
          </>
        )}
      </SettingsPageContainer>
    ),
    [
      state.key,
      state.hasPageChanges,
      state.isUpdateInProgress,
      state.tlsAll,
      state.tlsChanged,
      state.data,
      UseGetSettingsStateLogic.isLoading,
      UseGetSettingsStateLogic.isFailedToLoad,
      formatMessage,
      eventHandlers.helpConfig.onHelpClick,
      eventHandlers.helpConfig.onCloseHelp,
      eventHandlers.helpConfig.isOpen,
      eventHandlers.onCancelConfirm,
      eventHandlers.onSave,
      eventHandlers.onFormChange,
      eventHandlers.onBulkEdit,
      eventHandlers.onAddItem,
      eventHandlers.onRemoveItem,
      eventHandlers.setIsEncryptionTableDirty,
      isReadonly,
      classes.topSpacing,
      classes.flexWrapper,
      classes.bulkEdit,
      formatInboundMessage,
      state.hasNoDomainPolicyAccess
    ]
  )
}

export default Encryption
